import type { JSONFieldSchema, JSONFieldValues } from "@adrekic/fieldsjson"
import { EMPTY_OBJECT, type FieldsAttachmentModel, type UUID } from "@overmap-ai/common"
import { BaseOfflineFileModelApiService } from "@overmap-ai/core"
import { FieldSchema, type FieldSchemaConfig, type FieldValues } from "@overmap-ai/forms"
import { type DependencyList, useMemo } from "react"

import { useDebouncedCallbackWithFlush } from "../../hooks/utils"
import { AppSDK } from "../../sdk"
import type { RootState } from "../../store"
import {
	getFieldFilesAndPromisesFromAtachments,
	getFieldImagesAndPromisesFromAttachments,
	insertImagesAndPromisesToFields,
} from "./utils"

export const useHydrateFieldValuesWithAttachments = <TAttachment extends UUID<FieldsAttachmentModel>>(
	fieldSchema: FieldSchema,
	values: FieldValues,
	attachments: TAttachment[],
	service: BaseOfflineFileModelApiService<string, TAttachment, RootState, AppSDK>,
) => {
	const fieldFilesAndPromises = useMemo(() => {
		return getFieldFilesAndPromisesFromAtachments(attachments, service)
	}, [attachments, service])

	return useMemo(() => {
		return fieldSchema.insertFilesAndPromisesToFieldValues(values, fieldFilesAndPromises)
	}, [fieldFilesAndPromises, fieldSchema, values])
}

export const useHydrateFieldsWithAttachments = <TAttachment extends UUID<FieldsAttachmentModel>>(
	fieldSchema: FieldSchemaConfig,
	attachments: TAttachment[],
	service: BaseOfflineFileModelApiService<string, TAttachment, RootState, AppSDK>,
) => {
	const fieldImagesAndPromises = useMemo(() => {
		return getFieldImagesAndPromisesFromAttachments(attachments, service)
	}, [attachments, service])

	return useMemo(
		() => insertImagesAndPromisesToFields(fieldSchema, fieldImagesAndPromises),
		[fieldImagesAndPromises, fieldSchema],
	)
}

export const useDeserializeFieldValues = (
	fieldSchema: FieldSchema,
	serializedValues: JSONFieldValues = EMPTY_OBJECT,
) => {
	return useMemo(() => {
		if (serializedValues !== EMPTY_OBJECT && fieldSchema.areValuesPartial(serializedValues)) {
			console.warn(
				"Partial field values detected, should be handled gracefully: ",
				JSON.stringify(serializedValues, null, 2),
			)
		}

		return fieldSchema.deserializeValues(serializedValues)
	}, [fieldSchema, serializedValues])
}

export const useDeserializeFieldSchema = (fieldSchema: JSONFieldSchema) => {
	return useMemo(() => {
		return FieldSchema.deserialize(fieldSchema)
	}, [fieldSchema])
}

export const useFieldSchemaFromConfig = (fieldSchema: FieldSchemaConfig) => {
	return useMemo(() => {
		return FieldSchema.fromConfig(fieldSchema)
	}, [fieldSchema])
}

export const useDeserializeFieldSchemaConfig = (fieldSchema: JSONFieldSchema) => {
	return useMemo(() => {
		return FieldSchema.deserializeConfig(fieldSchema)
	}, [fieldSchema])
}

export const useFieldValuesChangeDebounced = <TFunction extends (values: FieldValues) => void>(
	callback: TFunction,
	deps: DependencyList,
) => {
	return useDebouncedCallbackWithFlush(callback, 500, deps)
}
